import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Package, AlertTriangle, CheckCircle, Euro, Edit2, Plus,
  Trash2, Save, X, Upload, Image as ImageIcon, ChevronDown, ChevronUp,
  Globe, Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2, Download, Type,
  Copy, ClipboardCheck, Maximize2, Minimize2, FolderOpen, Search, Eye,
  ChevronLeft, ChevronRight, ClipboardList, BarChart2, Code2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import {
  isAdmin as isAdminRole,
  isSuperAdmin as isSuperAdminRole,
  canViewCosts as canViewCostsRole,
} from '@/lib/roles';
import { r2Upload, r2Delete, r2Url } from '@/lib/r2';
import { formatCurrency, getStatusColor, cn } from '@/lib/utils';
import { writeAuditLog } from '@/lib/audit';
import { fetchUnitsSold } from '@/lib/api/salesEntries';
import { fetchBuildStock } from '@/lib/api/buildStock';
import { useListNavigation } from '@/lib/useListNavigation';
import { toast } from 'sonner';
import { PricingTool } from '@/components/PricingTool';
import { APMCard } from '@/components/APMCard';
import { generateAPMBanner, generateAPMBannerV2 } from '@/lib/generateAPMBanner';
import { useCurrentUser } from '@/lib/useCurrentUser';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { PrintMarketingTag } from '@/components/PrintMarketingTag';
import { PrintSpecSheet } from '@/components/PrintSpecSheet';

// ── Component search with auto-fill ──────────────────────────────────────────
function ComponentSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (sku: string, type: string, cost: number, desc: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState(value);
  const [results, setResults] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 380 });

  useEffect(() => { setSearch(value); }, [value]);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('components')
        .select('part_number, description, category, current_price')
        .or(`part_number.ilike.%${search}%,description.ilike.%${search}%`)
        .limit(12);
      setResults(data || []);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const openDropdown = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 380) });
    }
    setOpen(true);
  };

  return (
    <div>
      <input
        ref={inputRef}
        className="w-full border rounded px-2 py-1 text-xs font-mono bg-background"
        placeholder="Search part no. or description..."
        value={search}
        onChange={e => { setSearch(e.target.value); openDropdown(); }}
        onFocus={() => { if (search.length >= 2) openDropdown(); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && results.length > 0 && (
        <div
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto"
        >
          {results.map(c => (
            <button
              key={c.part_number}
              type="button"
              className="w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-b border-gray-100 last:border-0 flex gap-3 items-start"
              onMouseDown={() => {
                setSearch(c.part_number);
                setOpen(false);
                onChange(c.part_number, c.category || '', c.current_price || 0, c.description || '');
              }}
            >
              <span className="font-mono font-semibold shrink-0 text-primary">{c.part_number}</span>
              <span className="text-muted-foreground truncate">{c.description}</span>
              <span className="shrink-0 ml-auto text-muted-foreground italic">{c.category}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Constants ────────────────────────────────────────────────────────────────
import { getComponentSortRank } from '@/types/database';
import { HelpTooltip } from '@/components/HelpTooltip';
const categoryRank = (cat: string) => getComponentSortRank(cat ?? '');

type BomRow = { sku: string; type: string; qty: number; cost: number; desc: string };

// ── Main component ───────────────────────────────────────────────────────────
export function BuildDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id || id === 'new';
  const { hasPrev, hasNext, goPrev, goNext, position: listPosition } = useListNavigation('builds', '/builds');

  // ── State ─────────────────────────────────────────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [bomEditMode, setBomEditMode] = useState(false);

  // Header fields
  const [headerForm, setHeaderForm] = useState({
    sku: '', description: '', web_url: '', comments: '', is_quoted: false, rrp: '' as string,
  });

  // BOM rows (committed)
  const [bomRows, setBomRows] = useState<BomRow[]>([]);
  // Draft row
  const [draftSku, setDraftSku] = useState('');
  const [draftType, setDraftType] = useState('');
  const [draftDesc, setDraftDesc] = useState('');
  const [draftQty, setDraftQty] = useState(1);
  const [draftCost, setDraftCost] = useState(0);

  // Assembly history
  const [assemblyHistoryExpanded, setAssemblyHistoryExpanded] = useState(true);

  // Media bank
  const [mediaExpanded, setMediaExpanded] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState<string | null>(null);
  const [mediaBankOpen, setMediaBankOpen] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [bankUploading, setBankUploading] = useState(false);
  const bankFileRef = useRef<HTMLInputElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rrpSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Web listing editor
  const [webListingOpen, setWebListingOpen] = useState(false);
  const [webListingMaximized, setWebListingMaximized] = useState(false);
  const [webListingPreviewOpen, setWebListingPreviewOpen] = useState(false);
  const [webListingPreviewHtml, setWebListingPreviewHtml] = useState('');
  const [webListingPreviewMaximized, setWebListingPreviewMaximized] = useState(false);
  const [webListingHtmlMode, setWebListingHtmlMode] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const htmlSourceRef = useRef<HTMLTextAreaElement>(null);

  // Build a preview HTML document from editor innerHTML.
  // The editor stores typed raw HTML (e.g. APM banner code) as escaped text nodes.
  // This walks the DOM, detects blocks whose text content is raw HTML, and injects
  // them as real HTML so scripts and iframes render correctly in the preview.
  const buildPreviewHtml = (editorHtml: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorHtml, 'text/html');
    doc.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li, span').forEach(el => {
      const text = el.textContent?.trim() ?? '';
      // Detect blocks that hold raw HTML typed as plain text
      if (text.startsWith('<') && (text.includes('</') || text.includes('/>'))) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = text;
        el.replaceWith(wrapper);
      }
    });
    const body = doc.body.innerHTML;
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <style>
    body { font-family: system-ui, sans-serif; max-width: 900px; margin: 0 auto; padding: 24px; line-height: 1.6; color: #222; }
    h1 { font-size: 2em; margin: 0.5em 0; }
    h2 { font-size: 1.5em; margin: 0.75em 0 0.4em; }
    h3 { font-size: 1.2em; margin: 0.6em 0 0.3em; }
    ul, ol { padding-left: 1.5em; }
    img { max-width: 100%; height: auto; }
  </style>
</head>
<body>${body}</body>
</html>`;
  };

  // Marketing category visibility (admin-controlled, persisted per build)
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set(['acc', 'accessories']));
  const [hiddenCatsLoaded, setHiddenCatsLoaded] = useState(false);
  const [copiedSku, setCopiedSku] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  // Active configuration — which components are selected for costing
  // For categories with multiple options, only active ones count toward totals
  const [activeSkus, setActiveSkus] = useState<Set<string> | null>(null);

  const { data: currentUser } = useCurrentUser();

  const isAdmin = isAdminRole(currentUser?.role);
  const isSuperAdmin = isSuperAdminRole(currentUser?.role);
  const isMarketingOnly = currentUser?.role === 'marketing'; // excludes retail — for Web Details
  const canViewCosts = canViewCostsRole(currentUser?.role);

  const { data: build, isLoading: buildLoading } = useQuery({
    queryKey: ['build', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('builds')
        .select('id, sku, description, status, comments, units_built, is_quoted, date_created, created_at, rrp, marketing_hidden_categories, active_config_skus, web_url')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id && !isNew,
  });

  // BOM: prefer build_components table, fall back to components.build_sku
  const { data: savedBom, isLoading: bomLoading } = useQuery({
    queryKey: ['build-bom', id, build?.sku],
    queryFn: async () => {
      if (!id || isNew) return [];

      // Try build_components first
      const { data: bcRows } = await supabase
        .from('build_components')
        .select('id, component_sku, component_type, qty_per_build, cost_ex_vat')
        .eq('build_id', id);

      if (bcRows && bcRows.length > 0) {
        const skus = bcRows.map(r => r.component_sku).filter(Boolean);
        const descMap: Record<string, any> = {};
        if (skus.length > 0) {
          const { data: compData } = await supabase
            .from('components')
            .select('part_number, description, category, supplier, current_price, available_qty, on_order_qty, reserved_qty, expected_qty, status')
            .in('part_number', skus);
          (compData || []).forEach(c => { descMap[c.part_number] = c; });
        }
        return bcRows
          .sort((a, b) => categoryRank(a.component_type) - categoryRank(b.component_type))
          .map(r => ({
            sku: r.component_sku,
            type: r.component_type || '',
            qty: r.qty_per_build || 1,
            cost: r.cost_ex_vat || descMap[r.component_sku]?.current_price || 0,
            desc: descMap[r.component_sku]?.description || '',
            component: descMap[r.component_sku] || null,
          }));
      }

      // Fall back to components.build_sku
      if (!build?.sku) return [];
      const sku = build.sku;
      const { data, error } = await supabase
        .from('components')
        .select('id, part_number, description, category, supplier, available_qty, on_order_qty, reserved_qty, expected_qty, current_price, status, build_sku')
        .ilike('build_sku', `%${sku}%`);
      if (error) throw error;

      return (data ?? [])
        .filter(c => {
          const entries = (c.build_sku ?? '').split(',').map((s: string) => s.trim().replace(/-EOL$/i, ''));
          return entries.includes(sku);
        })
        .sort((a, b) => categoryRank(a.category) - categoryRank(b.category))
        .map(c => ({
          sku: c.part_number,
          type: c.category || '',
          qty: 1,
          cost: c.current_price ?? 0,
          desc: c.description || '',
          component: c,
        }));
    },
    enabled: !!id && !isNew,
  });

  // Media bank images
  const { data: mediaImages = [], refetch: refetchMedia } = useQuery({
    queryKey: ['build-media', id],
    queryFn: async () => {
      if (!id || isNew) return [];
      const { data, error } = await supabase
        .from('build_media')
        .select('id, file_path, file_name, caption, created_at, media_bank_id')
        .eq('build_id', id)
        .order('created_at', { ascending: true });
      if (error) {
        // Table may not exist yet — silently return empty
        console.warn('build_media query failed (table may not exist):', error.message);
        return [];
      }
      return (data || []).map((m: any) => ({
        ...m,
        url: r2Url(`build-media/${m.file_path}`),
      }));
    },
    enabled: !!id && !isNew,
  });

  // Case component images (pulled from the case in the BOM)
  const caseComponent = (savedBom ?? []).find(c => ['case', 'cas'].includes(c.type?.toLowerCase()));
  const { data: caseImages = [] } = useQuery({
    queryKey: ['component-media-for-build', caseComponent?.sku],
    queryFn: async () => {
      if (!caseComponent?.sku) return [];
      // Get component ID from part_number
      const { data: comp } = await supabase
        .from('components')
        .select('id')
        .eq('part_number', caseComponent.sku)
        .single();
      if (!comp) return [];
      const { data, error } = await supabase
        .from('component_media')
        .select('id, file_path, file_name, caption, created_at')
        .eq('component_id', comp.id)
        .order('created_at', { ascending: true });
      if (error) return [];
      return (data || []).map((m: any) => ({
        ...m,
        url: r2Url(`build-media/${m.file_path}`),
        source: 'case',
      }));
    },
    enabled: !!caseComponent?.sku && !isNew,
  });

  // Combined media: build-level images + case component images
  const allMedia = [...mediaImages, ...caseImages];

  // ── Units sold ────────────────────────────────────────────────────────────
  // Via RPC, not sales_entries: the table is admin-only but this page is not.
  // Same query key as BuildsPage, so arriving from the list costs nothing and
  // a direct load fetches once. build_stock() returns every build; picking one
  // out here keeps the two pages on one cached answer rather than adding a
  // second RPC that could disagree with the first.
  const { data: stockByBuild } = useQuery({
    queryKey: ['build-stock'],
    queryFn: fetchBuildStock,
    staleTime: 60_000,
  });
  const stock = id ? stockByBuild?.get(id) : undefined;

  const { data: unitsSold } = useQuery({
    queryKey: ['build-units-sold', build?.sku],
    queryFn: () => fetchUnitsSold(build!.sku),
    enabled: !!build?.sku && !isNew,
    staleTime: 5 * 60_000,
  });

  // ── APM (benchmark scores) ────────────────────────────────────────────────
  const { data: apmData } = useQuery({
    queryKey: ['build-apm', id, build?.sku],
    queryFn: async () => {
      // Try live scores from assembly_order_benchmarks first
      // Check by build_id AND build_sku (legacy orders may only have build_sku set)
      const buildSku = build?.sku ?? null;
      const { data: ordersByBuildId } = await supabase
        .from('assembly_orders')
        .select('id, order_number')
        .eq('build_id', id!);
      let orders = ordersByBuildId ?? [];
      if (buildSku) {
        const { data: ordersBySku } = await supabase
          .from('assembly_orders')
          .select('id, order_number')
          .eq('build_sku', buildSku)
          .is('build_id', null);
        if (ordersBySku && ordersBySku.length > 0) {
          const existingIds = new Set(orders.map(o => o.id));
          orders = [...orders, ...ordersBySku.filter(o => !existingIds.has(o.id))];
        }
      }
      if (orders.length > 0) {
        const orderIds = orders.map(o => o.id);
        const orderNumberMap: Record<string, string> = Object.fromEntries(
          orders.map(o => [o.id, o.order_number ?? o.id])
        );
        const { data: benchmarks } = await supabase
          .from('assembly_order_benchmarks')
          .select('assembly_order_id, passmark_rating, cpu_mark, graphics_2d_mark, graphics_3d_mark, memory_mark, disk_mark')
          .in('assembly_order_id', orderIds);
        if (benchmarks && benchmarks.length > 0) {
          const avg = (key: string) => {
            const vals = benchmarks.map((b: any) => b[key]).filter((v: any) => v != null && v > 0);
            return vals.length > 0 ? Math.round(vals.reduce((s: number, v: number) => s + v, 0) / vals.length) : null;
          };
          // Only count benchmarks that have a valid passmark_rating
          const validBenchmarks = benchmarks.filter((b: any) => b.passmark_rating != null && b.passmark_rating > 0);
          // Group valid benchmarks by order for the instances list
          const byOrder: Record<string, { scores: number[] }> = {};
          for (const b of validBenchmarks) {
            if (!byOrder[b.assembly_order_id]) byOrder[b.assembly_order_id] = { scores: [] };
            byOrder[b.assembly_order_id].scores.push(b.passmark_rating);
          }
          const instances = Object.entries(byOrder).map(([orderId, { scores }]) => ({
            assembly_order_id: orderId,
            order_number: orderNumberMap[orderId] ?? orderId,
            passmark_rating: Math.round(scores.reduce((s, v) => s + v, 0) / scores.length),
            unit_count: scores.length,
          }));
          return {
            passmark_rating: avg('passmark_rating'),
            cpu_mark: avg('cpu_mark'),
            graphics_2d_mark: avg('graphics_2d_mark'),
            graphics_3d_mark: avg('graphics_3d_mark'),
            memory_mark: avg('memory_mark'),
            disk_mark: avg('disk_mark'),
            sample_count: validBenchmarks.length,
            instances,
            source: 'live' as const,
          };
        }
      }
      // Fall back to historical build_benchmarks
      const { data: historical } = await supabase
        .from('build_benchmarks')
        .select('passmark_rating, cpu_mark, graphics_2d_mark, graphics_3d_mark, memory_mark, disk_mark, sample_count')
        .eq('build_id', id!)
        .maybeSingle();
      if (historical) return { ...historical, source: 'historical' as const };
      return null;
    },
    enabled: !!id && !isNew && !!build,
  });

  // ── B2C display names ─────────────────────────────────────────────────────
  const bomSkus = (savedBom ?? []).map((r: any) => r.sku).filter(Boolean);
  const { data: b2cNames = {} } = useQuery({
    queryKey: ['b2c-display-names', bomSkus],
    queryFn: async () => {
      if (bomSkus.length === 0) return {};
      const { data } = await supabase
        .from('component_display_names')
        .select('part_number, display_name_b2c')
        .in('part_number', bomSkus);
      const map: Record<string, string | null> = {};
      (data ?? []).forEach((r: any) => { map[r.part_number] = r.display_name_b2c; });
      return map;
    },
    enabled: (isAdmin || isMarketingOnly) && bomSkus.length > 0,
  });
  const missingB2cCount = isMarketingOnly ? bomSkus.filter(sku => !b2cNames[sku]).length : 0;

  // ── Inline display name editing (admin) ───────────────────────────────────
  const [showDisplayNamesDialog, setShowDisplayNamesDialog] = useState(false);
  const [editedDisplayNames, setEditedDisplayNames] = useState<Record<string, string>>({});
  const displayNamesDirty = Object.keys(editedDisplayNames).length > 0;

  const saveDisplayNamesMutation = useMutation({
    mutationFn: async () => {
      const upserts = Object.entries(editedDisplayNames).map(([sku, name]) => ({
        part_number: sku,
        display_name_b2c: name,
        updated_at: new Date().toISOString(),
      }));
      const { error } = await supabase
        .from('component_display_names')
        .upsert(upserts, { onConflict: 'part_number', ignoreDuplicates: false });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['b2c-display-names'] });
      setEditedDisplayNames({});
      toast.success('Display names saved');
    },
    onError: (err: Error) => toast.error('Save failed: ' + err.message),
  });

  // ── Assembly history ──────────────────────────────────────────────────
  const { data: assemblyHistory } = useQuery({
    queryKey: ['build-assembly-history', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assembly_orders')
        .select(`
          id, order_number, status, serial_number, quantity,
          to_number, notes, created_at, started_at, completed_at,
          stage_timestamps, build_type,
          technician:technicians!technician_id(name, initials)
        `)
        .eq('build_id', id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!id && !isNew,
  });

  // ── Web listing content ─────────────────────────────────────────────────
  const { data: webListing, refetch: refetchWebListing } = useQuery({
    queryKey: ['build-web-listing', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('build_web_listings')
        .select('id, content_html, updated_at')
        .eq('build_id', id!)
        .maybeSingle();
      if (error) {
        console.warn('build_web_listings query failed:', error.message);
        return null;
      }
      return data;
    },
    enabled: !!id && !isNew && (isAdmin || isMarketingOnly),
  });

  const saveWebListingMutation = useMutation({
    mutationFn: async (html: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('build_web_listings')
        .upsert({
          build_id: id!,
          content_html: html,
          updated_at: new Date().toISOString(),
          updated_by: user?.id || null,
        }, { onConflict: 'build_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      refetchWebListing();
      toast.success('Web listing saved');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to save web listing'),
  });

  // ── Initialize edit forms from loaded data ────────────────────────────────
  useEffect(() => {
    if (build && !isNew) {
      setHeaderForm({
        sku: build.sku || '',
        description: build.description || '',
        web_url: (build as any).web_url || '',
        comments: build.comments || '',
        is_quoted: build.is_quoted || false,
        rrp: build.rrp != null ? String(build.rrp) : '',
      });
    }
  }, [build, isNew]);

  useEffect(() => {
    if (savedBom?.length) {
      setBomRows(savedBom.map(r => ({ sku: r.sku, type: r.type, qty: r.qty, cost: r.cost, desc: r.desc })));
    }
  }, [savedBom]);

  // Load persisted marketing hidden categories
  useEffect(() => {
    if (build && !hiddenCatsLoaded) {
      const stored = (build as any).marketing_hidden_categories || '';
      const cats = stored ? stored.split(',').map((s: string) => s.trim()).filter(Boolean) : ['acc', 'accessories'];
      setHiddenCategories(new Set(cats));
      setHiddenCatsLoaded(true);
    }
  }, [build, hiddenCatsLoaded]);

  // ── Auto-init active config: load from DB or default to first per category
  useEffect(() => {
    if (!savedBom?.length || activeSkus !== null) return;
    const stored = (build as any)?.active_config_skus || '';
    if (stored) {
      const skus = stored.split(',').map((s: string) => s.trim()).filter(Boolean);
      setActiveSkus(new Set(skus));
    } else {
      const seen = new Set<string>();
      const initial = new Set<string>();
      for (const bc of savedBom) {
        const cat = (bc.type || '').toLowerCase();
        if (!seen.has(cat)) {
          initial.add(bc.sku);
          seen.add(cat);
        }
      }
      setActiveSkus(initial);
    }
  }, [savedBom, activeSkus, build]);

  // ── Derived (only sums active components) ──────────────────────────────
  const totals = useMemo(() => {
    const bom = savedBom ?? [];
    const active = activeSkus ?? new Set(bom.map(b => b.sku));
    return bom.reduce(
      (acc, bc) => {
        const isActive = active.has(bc.sku);
        const qty = bc.qty || 1;
        const cost = bc.cost || 0;
        if (isActive) acc.totalCost += cost * qty;
        const comp = (bc as any).component;
        if (comp && isActive) {
          const avail = comp.available_qty ?? 0;
          acc.maxBuildable = Math.min(acc.maxBuildable, qty > 0 ? Math.floor(avail / qty) : 999);
          if (avail === 0) acc.outOfStock++;
          else if (avail < qty) acc.lowStock++;
        }
        return acc;
      },
      { totalCost: 0, maxBuildable: 999, outOfStock: 0, lowStock: 0 }
    );
  }, [savedBom, activeSkus]);

  // ── Effective status (auto-computed) ─────────────────────────────────────
  const KEY_CATS = useMemo(() => new Set(['cpu', 'gpu', 'case', 'cas']), []);
  const { data: recentOrderExists } = useQuery({
    queryKey: ['build-recent-orders', id],
    queryFn: async () => {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const { count } = await supabase
        .from('assembly_orders')
        .select('id', { count: 'exact', head: true })
        .eq('build_id', id!)
        .eq('status', 'completed')
        .gte('completed_at', sixMonthsAgo.toISOString());
      return (count ?? 0) > 0;
    },
    enabled: !!id && !isNew,
  });

  const effectiveStatus = useMemo(() => {
    if (!savedBom || isNew) return build?.status || 'Active';
    const keyComps = savedBom.filter((bc: any) => KEY_CATS.has((bc.type || '').toLowerCase()));
    const keyComponentsOk = keyComps.length > 0 && keyComps.every((bc: any) => {
      const comp = bc.component;
      if (!comp) return false;
      return ((comp.available_qty ?? 0) + (comp.reserved_qty ?? 0) + (comp.on_order_qty ?? 0)) > 0;
    });
    return (keyComponentsOk || recentOrderExists) ? 'Active' : 'Inactive';
  }, [savedBom, recentOrderExists, isNew, build?.status, KEY_CATS]);

  // Auto-sync status to DB if changed
  useEffect(() => {
    if (!id || isNew || !build || !effectiveStatus) return;
    if (build.status !== effectiveStatus) {
      supabase.from('builds').update({ status: effectiveStatus }).eq('id', id).then(() => {
        queryClient.invalidateQueries({ queryKey: ['build', id] });
      });
    }
  }, [effectiveStatus, build?.status, id, isNew, queryClient]);

  // ── Create build mutation ─────────────────────────────────────────────────
  const createBuildMutation = useMutation({
    mutationFn: async () => {
      if (!headerForm.sku.trim()) throw new Error('Build SKU is required');

      // Merge draft row
      const allRows = [...bomRows];
      if (draftSku.trim()) {
        allRows.push({ sku: draftSku.trim(), type: draftType.trim(), qty: draftQty, cost: draftCost, desc: draftDesc });
      }

      const { data, error } = await supabase
        .from('builds')
        .insert({
          sku: headerForm.sku.trim(),
          description: headerForm.description.trim(),
          web_url: headerForm.web_url.trim() || null,
          status: 'Active',
          comments: headerForm.comments.trim() || null,
          is_quoted: headerForm.is_quoted,
          rrp: headerForm.rrp ? parseFloat(headerForm.rrp) : null,
          date_created: new Date().toISOString().split('T')[0],
        })
        .select('id, sku')
        .single();
      if (error) throw error;

      // Insert build_components
      if (allRows.length > 0) {
        const { error: bomErr } = await supabase.from('build_components').insert(
          allRows.map(r => ({
            build_id: data.id,
            component_sku: r.sku,
            component_type: r.type,
            qty_per_build: r.qty,
            cost_ex_vat: r.cost,
          }))
        );
        if (bomErr) throw bomErr;
      }

      await writeAuditLog({
        action: 'build_created',
        entity_type: 'build',
        entity_id: data.id,
        entity_label: data.sku,
        new_value: { sku: data.sku, status: 'Active', components: allRows.length },
      });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['builds'] });
      toast.success(`Build ${data.sku} created`);
      // replace, not push: Back from a new build belongs on the builds list,
      // not on the create form that made it.
      navigate(`/builds/${data.id}`, { replace: true });
    },
    onError: (e: any) => toast.error(e.message || 'Failed to create build'),
  });

  // ── Update build header mutation ──────────────────────────────────────────
  const updateHeaderMutation = useMutation({
    mutationFn: async () => {
      if (!id || isNew) throw new Error('No build to update');
      const { error } = await supabase
        .from('builds')
        .update({
          sku: headerForm.sku.trim(),
          description: headerForm.description.trim(),
          web_url: headerForm.web_url.trim() || null,
          comments: headerForm.comments.trim() || null,
          is_quoted: headerForm.is_quoted,
          rrp: headerForm.rrp ? parseFloat(headerForm.rrp) : null,
        })
        .eq('id', id);
      if (error) throw error;

      await writeAuditLog({
        action: 'build_updated',
        entity_type: 'build',
        entity_id: id,
        entity_label: headerForm.sku,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['build', id] });
      queryClient.invalidateQueries({ queryKey: ['builds'] });
      setEditMode(false);
      toast.success('Build updated');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update'),
  });

  // ── Save BOM mutation ─────────────────────────────────────────────────────
  const saveBomMutation = useMutation({
    mutationFn: async () => {
      if (!id || isNew) throw new Error('No build to update');

      // Merge draft row
      const allRows = [...bomRows];
      if (draftSku.trim()) {
        allRows.push({ sku: draftSku.trim(), type: draftType.trim(), qty: draftQty, cost: draftCost, desc: draftDesc });
      }

      // Replace all build_components
      await supabase.from('build_components').delete().eq('build_id', id);
      if (allRows.length > 0) {
        const { error } = await supabase.from('build_components').insert(
          allRows.map(r => ({
            build_id: id,
            component_sku: r.sku,
            component_type: r.type,
            qty_per_build: r.qty,
            cost_ex_vat: r.cost,
          }))
        );
        if (error) throw error;
      }

      await writeAuditLog({
        action: 'build_bom_updated',
        entity_type: 'build',
        entity_id: id,
        entity_label: build?.sku,
        notes: `BOM updated: ${allRows.length} components`,
      });

      return allRows;
    },
    onSuccess: (allRows) => {
      setBomRows(allRows);
      clearDraft();
      setBomEditMode(false);
      queryClient.invalidateQueries({ queryKey: ['build-bom', id] });
      toast.success('Bill of Materials saved');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to save BOM'),
  });

  // ── Media upload ──────────────────────────────────────────────────────────
  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !id || isNew) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop() || 'jpg';
        const filePath = `${id}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;

        await r2Upload(`build-media/${filePath}`, file, file.type);

        const { error: dbErr } = await supabase.from('build_media').insert({
          build_id: id,
          file_path: filePath,
          file_name: file.name,
        });
        if (dbErr) throw dbErr;
      }
      refetchMedia();
      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded`);
    } catch (err: any) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteImageMutation = useMutation({
    mutationFn: async (imageId: string) => {
      const img = mediaImages.find(m => m.id === imageId);
      // Only delete from storage if it's NOT a bank-linked image
      if (img?.file_path && !img.media_bank_id) {
        await r2Delete(`build-media/${img.file_path}`);
      }
      const { error } = await supabase.from('build_media').delete().eq('id', imageId);
      if (error) throw error;
    },
    onSuccess: () => {
      refetchMedia();
      setDeleteImageId(null);
      toast.success('Image deleted');
    },
    onError: (e: any) => toast.error(`Delete failed: ${e.message}`),
  });

  // ── Media bank ──────────────────────────────────────────────────────────
  const { data: mediaBankImages = [], refetch: refetchBank } = useQuery({
    queryKey: ['media-bank'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_bank')
        .select('id, file_path, file_name, label, created_at')
        .order('created_at', { ascending: false });
      if (error) { console.warn('media_bank query failed:', error.message); return []; }
      return (data || []).map((m: any) => ({
        ...m,
        url: r2Url(`build-media/${m.file_path}`),
      }));
    },
    enabled: mediaBankOpen,
  });

  const filteredBankImages = mediaBankImages.filter((img: any) => {
    if (!bankSearch) return true;
    const s = bankSearch.toLowerCase();
    return img.file_name?.toLowerCase().includes(s) || img.label?.toLowerCase().includes(s);
  });

  // Which bank image IDs are already linked to this build?
  const linkedBankIds = new Set(mediaImages.filter((m: any) => m.media_bank_id).map((m: any) => m.media_bank_id));

  const handleBankUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setBankUploading(true);
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop() || 'jpg';
        const filePath = `media-bank/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
        await r2Upload(`build-media/${filePath}`, file, file.type);
        const { error: dbErr } = await supabase.from('media_bank').insert({
          file_path: filePath,
          file_name: file.name,
          label: file.name.replace(/\.[^.]+$/, ''),
        });
        if (dbErr) throw dbErr;
      }
      refetchBank();
      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} added to media bank`);
    } catch (err: any) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setBankUploading(false);
      if (bankFileRef.current) bankFileRef.current.value = '';
    }
  };

  const linkBankImage = async (bankImg: any) => {
    try {
      const { error } = await supabase.from('build_media').insert({
        build_id: id,
        file_path: bankImg.file_path,
        file_name: bankImg.file_name,
        media_bank_id: bankImg.id,
      });
      if (error) throw error;
      refetchMedia();
      toast.success('Image linked to build');
    } catch (err: any) {
      toast.error(`Failed to link: ${err.message}`);
    }
  };

  const deleteBankImage = async (bankImgId: string) => {
    const img = mediaBankImages.find((m: any) => m.id === bankImgId);
    if (!img) return;
    try {
      await r2Delete(`build-media/${img.file_path}`);
      const { error } = await supabase.from('media_bank').delete().eq('id', bankImgId);
      if (error) throw error;
      refetchBank();
      refetchMedia();
      toast.success('Image deleted from media bank');
    } catch (err: any) {
      toast.error(`Delete failed: ${err.message}`);
    }
  };

  // ── Delete build (super_admin only) ──────────────────────────────────────
  const deleteBuildMutation = useMutation({
    mutationFn: async () => {
      // Delete BOM rows first
      await supabase.from('build_components').delete().eq('build_id', id);
      // Delete build media
      if (mediaImages.length > 0) {
        const paths = mediaImages.filter(m => m.file_path).map(m => m.file_path);
        if (paths.length) await Promise.all(paths.map(p => r2Delete(`build-media/${p}`)));
        await supabase.from('build_media').delete().eq('build_id', id);
      }
      // Delete the build
      const { error } = await supabase.from('builds').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await writeAuditLog({
        action: 'build_deleted',
        entity_type: 'build',
        entity_id: id,
        entity_label: build?.sku,
        old_value: { sku: build?.sku, description: build?.description },
      });
      toast.success('Build deleted');
      navigate('/builds');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to delete build'),
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const clearDraft = () => {
    setDraftSku(''); setDraftType(''); setDraftDesc(''); setDraftQty(1); setDraftCost(0);
  };

  const commitDraftRow = () => {
    if (!draftSku.trim()) return;
    setBomRows(prev => [...prev, { sku: draftSku.trim(), type: draftType.trim(), qty: draftQty, cost: draftCost, desc: draftDesc }]);
    clearDraft();
  };

  const startBomEdit = () => {
    const rows = (savedBom ?? []).map(r => ({ sku: r.sku, type: r.type, qty: r.qty, cost: r.cost, desc: r.desc }));
    setBomRows(rows);
    clearDraft();
    setBomEditMode(true);
  };

  // ── Loading / not found ───────────────────────────────────────────────────
  if (!isNew && buildLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!isNew && !build) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Build not found</p>
        <Button className="mt-4" onClick={() => navigate(-1)}>Back to Builds</Button>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* ── Navigation ── */}
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        {!isNew && listPosition && (
          <>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={goPrev} disabled={!hasPrev} title="Previous build">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground px-1 min-w-[3rem] text-center">{listPosition}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={goNext} disabled={!hasNext} title="Next build">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      {/* ── Header ── */}
      <div>
        {isNew ? (
          <div className="space-y-4">
            <h1 className="text-3xl font-bold">New Build</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label className="text-xs font-medium text-muted-foreground">SKU *</label>
                <Input
                  className="font-mono"
                  placeholder="e.g. AOI-1A-G4-R1"
                  value={headerForm.sku}
                  onChange={e => setHeaderForm(p => ({ ...p, sku: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Description</label>
                <Input
                  placeholder="Build description"
                  value={headerForm.description}
                  onChange={e => setHeaderForm(p => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Web URL</label>
                <Input
                  type="url"
                  placeholder="https://..."
                  value={headerForm.web_url}
                  onChange={e => setHeaderForm(p => ({ ...p, web_url: e.target.value }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Comments</label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm bg-background min-h-[60px]"
                  placeholder="Optional notes..."
                  value={headerForm.comments}
                  onChange={e => setHeaderForm(p => ({ ...p, comments: e.target.value }))}
                />
              </div>
            </div>
          </div>
        ) : editMode ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">Edit Build</h1>
              <Button size="sm" onClick={() => updateHeaderMutation.mutate()} disabled={updateHeaderMutation.isPending}>
                <Save className="h-4 w-4 mr-1" />{updateHeaderMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditMode(false); if (build) setHeaderForm({ sku: build.sku, description: build.description || '', web_url: (build as any).web_url || '', comments: build.comments || '', is_quoted: build.is_quoted || false, rrp: build.rrp != null ? String(build.rrp) : '' }); }}>
                <X className="h-4 w-4 mr-1" />Cancel
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label className="text-xs font-medium text-muted-foreground">SKU</label>
                <Input className="font-mono" value={headerForm.sku} onChange={e => setHeaderForm(p => ({ ...p, sku: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Description</label>
                <Input value={headerForm.description} onChange={e => setHeaderForm(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Web URL</label>
                <Input
                  type="url"
                  placeholder="https://..."
                  value={headerForm.web_url}
                  onChange={e => setHeaderForm(p => ({ ...p, web_url: e.target.value }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Comments</label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm bg-background min-h-[60px]"
                  value={headerForm.comments}
                  onChange={e => setHeaderForm(p => ({ ...p, comments: e.target.value }))}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            {/* Build / case image thumbnail — prefer build images, fall back to case */}
            {allMedia.length > 0 && (
              <div className="shrink-0 cursor-pointer" onClick={() => setLightboxUrl((mediaImages.length > 0 ? mediaImages[0] : allMedia[0]).url)}>
                <img
                  src={(mediaImages.length > 0 ? mediaImages[0] : allMedia[0]).url}
                  alt={mediaImages.length > 0 ? 'Build' : 'Case'}
                  className="w-full max-w-[250px] sm:w-48 sm:h-48 object-cover rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold font-mono">{build!.sku}</h1>
                <Badge className={getStatusColor(effectiveStatus)}>{effectiveStatus}</Badge>
                <HelpTooltip tipKey="build.status" />
                {build!.is_quoted && <Badge variant="outline" className="text-green-700 border-green-300">Quoted</Badge>}
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                    <Edit2 className="h-4 w-4 mr-1" />Edit
                  </Button>
                )}
                {(isAdmin || isMarketingOnly) && (
                  <Button variant="outline" size="sm" onClick={() => {
                    setWebListingOpen(true);
                    // Load content into editor after dialog opens
                    setTimeout(() => {
                      if (editorRef.current) {
                        editorRef.current.innerHTML = webListing?.content_html || '';
                      }
                    }, 50);
                  }}>
                    <Globe className="h-4 w-4 mr-1" />Web Details
                  </Button>
                )}
                {(isAdmin || isMarketingOnly) && (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!apmData || apmData.passmark_rating == null}
                        title={!apmData || apmData.passmark_rating == null ? 'No APM data available for this build' : 'Generate APM banner and copy embed code'}
                      >
                        <BarChart2 className="h-4 w-4 mr-1" />APM Banner<ChevronDown className="h-3 w-3 ml-1 opacity-60" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        align="end"
                        sideOffset={4}
                        className="z-50 min-w-[200px] rounded-md border bg-popover p-1 shadow-md"
                      >
                        <DropdownMenu.Item
                          className="flex flex-col cursor-pointer rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent"
                          onSelect={async () => {
                            try {
                              await generateAPMBanner(apmData!, build!.sku, build!.id);
                              toast.success('Embed code copied to clipboard — paste it into your listing body');
                            } catch {
                              toast.error('Failed to generate APM banner');
                            }
                          }}
                        >
                          <span className="font-medium flex items-center gap-1.5">
                            <Code2 className="h-3.5 w-3.5" /> Standard
                          </span>
                          <span className="text-xs text-muted-foreground mt-0.5">Background image embedded</span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          className="flex flex-col cursor-pointer rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent"
                          onSelect={async () => {
                            try {
                              await generateAPMBannerV2(apmData!, build!.sku, build!.id);
                              toast.success('Embed code copied to clipboard — paste it into your listing body');
                            } catch {
                              toast.error('Failed to generate APM banner');
                            }
                          }}
                        >
                          <span className="font-medium flex items-center gap-1.5">
                            <Code2 className="h-3.5 w-3.5" /> Optimised
                          </span>
                          <span className="text-xs text-muted-foreground mt-0.5">Background image hosted externally</span>
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                )}
                <PrintSpecSheet
                  build={{ id: build!.id, sku: build!.sku, description: build!.description }}
                  activeConfigSkus={activeSkus}
                  size="sm"
                />
                {build!.rrp != null && (
                  <PrintMarketingTag
                    build={{ id: build!.id, sku: build!.sku, rrp: build!.rrp, description: build!.description }}
                    activeConfigSkus={activeSkus}
                    size="sm"
                  />
                )}
                {isSuperAdmin && (
                  <Button variant="destructive" size="sm" onClick={() => setShowDeleteConfirm(true)}>
                    <Trash2 className="h-4 w-4 mr-1" />Delete
                  </Button>
                )}
              </div>
              <p className="text-muted-foreground mt-1">{build!.description || '---'}</p>
              {(build as any).web_url && (
                <p className="text-sm mt-1">
                  <a href={(build as any).web_url} target="_blank" rel="noreferrer" className="text-primary underline break-all">
                    {(build as any).web_url}
                  </a>
                </p>
              )}
              {build!.comments && <p className="text-sm text-muted-foreground mt-1 italic">{build!.comments}</p>}
              <div className="flex gap-4 mt-1 text-sm text-muted-foreground">
                {(() => {
                  const dynamicUnitsBuilt = (assemblyHistory ?? [])
                    .filter(o => o.status === 'completed' || o.status === 'Posted')
                    .reduce((sum, o) => sum + ((o as any).quantity ?? 1), 0);
                  return dynamicUnitsBuilt > 0
                    ? <span>Units built: <strong>{dynamicUnitsBuilt}</strong></span>
                    : null;
                })()}
                {stock?.on_hand != null && (
                  <span
                    title={
                      `${stock.opening_qty} per Business Central on ${stock.opening_at}` +
                      `, +${stock.built_since} built since, -${stock.sold_since} sold since` +
                      (stock.on_display > 0
                        ? `. ${stock.on_display} on demo stations, ${stock.available} available to sell.`
                        : '')
                    }
                  >
                    In stock: <strong>{stock.on_hand}</strong>
                    {stock.on_display > 0 && (
                      <span className="ml-1">({stock.on_display} on display)</span>
                    )}
                  </span>
                )}
                {unitsSold != null && (
                  <span title="All time, net of returns">
                    Units sold: <strong>{unitsSold.units_net}</strong>
                  </span>
                )}
                {build!.date_created && (
                  <span>Created: <strong>{new Date(build!.date_created).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
                )}
                {canViewCosts && build!.rrp != null && (
                  <span>RRP: <strong>{formatCurrency(build!.rrp)}</strong></span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Summary cards (view mode only) ── */}
      {!isNew && !editMode && (
        <div className={`grid gap-4 ${canViewCosts ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}>
          {canViewCosts && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Euro className="h-4 w-4" />Total BOM Cost <HelpTooltip tipKey="build.cost" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(totals.totalCost)}</div>
                <p className="text-xs text-muted-foreground">Ex-VAT component cost</p>
              </CardContent>
            </Card>
          )}
          {canViewCosts && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />Max Buildable
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${totals.maxBuildable === 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {totals.maxBuildable === 999 ? '---' : totals.maxBuildable}
                </div>
                <p className="text-xs text-muted-foreground">Based on current stock</p>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />Stock Issues
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totals.outOfStock + totals.lowStock}</div>
              <p className="text-xs text-muted-foreground">{totals.outOfStock} out / {totals.lowStock} low</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />Components
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{savedBom?.length ?? 0}</div>
              <p className="text-xs text-muted-foreground">In bill of materials</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── APM Card ── */}
      {!isNew && !editMode && (apmData?.passmark_rating != null || isAdmin) && (
        <APMCard
          buildId={id!}
          apmData={apmData ?? null}
          isAdmin={isAdmin}
          queryClient={queryClient}
          buildSku={build?.sku}
        />
      )}

      {/* ── Marketing B2C warning ── */}
      {isMarketingOnly && missingB2cCount > 0 && !isNew && (
        <div className="flex items-center gap-2 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2.5">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {missingB2cCount} component{missingB2cCount !== 1 ? 's' : ''} missing B2C display name{missingB2cCount !== 1 ? 's' : ''} — showing raw descriptions instead.
        </div>
      )}

      {/* ── Pricing Tool ── */}
      {!isNew && !editMode && canViewCosts && (savedBom?.length ?? 0) > 0 && (
        <PricingTool
          totalCost={totals.totalCost}
          rrpValue={headerForm.rrp}
          onRrpChange={(rrp) => {
            setHeaderForm(p => ({ ...p, rrp }));
            // Debounce auto-save to avoid overwriting input mid-keystroke
            if (rrpSaveTimer.current) clearTimeout(rrpSaveTimer.current);
            rrpSaveTimer.current = setTimeout(async () => {
              if (id && rrp) {
                await supabase.from('builds').update({ rrp: parseFloat(rrp) }).eq('id', id);
                queryClient.invalidateQueries({ queryKey: ['build', id] });
              }
            }, 800);
          }}
          components={(savedBom ?? []).filter(r => activeSkus?.has(r.sku) ?? true).map(r => ({
            partNumber: r.sku,
            description: r.desc || '',
            category: r.type || '',
            cost: (r.cost || 0) * (r.qty || 1),
          }))}
          filledCount={(savedBom ?? []).filter(r => activeSkus?.has(r.sku) ?? true).length}
        />
      )}

      {/* ── Bill of Materials ── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Bill of Materials <HelpTooltip tipKey="build.bom" /></CardTitle>
            <div className="flex gap-2">
              {isMarketingOnly && !isNew && !bomEditMode && (savedBom?.length ?? 0) > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const visibleRows = (savedBom ?? []).filter((bc: any) =>
                      !hiddenCategories.has((bc.type || '').toLowerCase())
                    );
                    const lines = visibleRows.map((bc: any) => {
                      return b2cNames[bc.sku] || bc.desc || bc.component?.description || bc.sku;
                    });
                    navigator.clipboard.writeText(lines.join('\n'));
                    setCopiedAll(true);
                    setTimeout(() => setCopiedAll(false), 2000);
                  }}
                >
                  {copiedAll ? <ClipboardCheck className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copiedAll ? 'Copied!' : 'Copy All'}
                </Button>
              )}
              {!isNew && isAdmin && !bomEditMode && (savedBom?.length ?? 0) > 0 && (
                <Button variant="outline" size="sm" onClick={() => {
                  setEditedDisplayNames({});
                  setShowDisplayNamesDialog(true);
                }}>
                  <Type className="h-4 w-4 mr-1" />Display Names
                </Button>
              )}
              {!isNew && isAdmin && !bomEditMode && (
                <Button variant="outline" size="sm" onClick={startBomEdit}>
                  <Edit2 className="h-4 w-4 mr-1" />Edit BOM
                </Button>
              )}
            </div>
            {!isNew && bomEditMode && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setBomEditMode(false)}>Cancel</Button>
                <Button size="sm" onClick={() => saveBomMutation.mutate()} disabled={saveBomMutation.isPending}>
                  <Save className="h-4 w-4 mr-1" />{saveBomMutation.isPending ? 'Saving...' : 'Save BOM'}
                </Button>
              </div>
            )}
          </div>
          {/* Category visibility toggles (admin controls what marketing sees) */}
          {isAdmin && !isNew && !bomEditMode && (savedBom?.length ?? 0) > 0 && (() => {
            const cats = [...new Set((savedBom ?? []).map((bc: any) => (bc.type || '').toLowerCase()).filter(Boolean))];
            cats.sort((a, b) => categoryRank(a) - categoryRank(b));
            const CAT_LABELS: Record<string, string> = {
              cpu: 'CPU', 'cpu cooler': 'Cooler', fan: 'Cooler', mb: 'Motherboard', ram: 'RAM',
              storage: 'Storage', rom: 'Storage', gpu: 'GPU', psu: 'PSU', os: 'OS',
              case: 'Case', cas: 'Case', accessories: 'Accessories', acc: 'Accessories',
            };
            return (
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-xs text-muted-foreground self-center mr-1">Marketing sees:</span>
                {cats.map(cat => {
                  const isHidden = hiddenCategories.has(cat);
                  return (
                    <button
                      key={cat}
                      className={cn(
                        'text-xs px-2 py-0.5 rounded-full border transition-colors',
                        isHidden ? 'bg-muted text-muted-foreground border-muted' : 'bg-primary/10 text-primary border-primary/20',
                      )}
                      onClick={() => {
                        setHiddenCategories(prev => {
                          const next = new Set(prev);
                          if (next.has(cat)) next.delete(cat); else next.add(cat);
                          // Persist to DB
                          const csv = [...next].join(',');
                          supabase.from('builds').update({ marketing_hidden_categories: csv }).eq('id', id).then(() => {
                            queryClient.invalidateQueries({ queryKey: ['build', id] });
                          });
                          return next;
                        });
                      }}
                    >
                      {CAT_LABELS[cat] || cat}
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </CardHeader>
        <CardContent className="p-0">
          {!isNew && bomLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : (isNew || bomEditMode) ? (
            /* ── Editable BOM table ── */
            <div className="p-4">
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2 font-semibold text-xs w-[100px]">Type</th>
                      <th className="text-left p-2 font-semibold text-xs w-[220px]">Part Number</th>
                      <th className="text-left p-2 font-semibold text-xs">Description</th>
                      <th className="text-right p-2 font-semibold text-xs w-[60px]">Qty</th>
                      {canViewCosts && <th className="text-right p-2 font-semibold text-xs w-[100px]">Cost (ex VAT)</th>}
                      <th className="p-2 w-[40px]"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bomRows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-muted/30">
                        <td className="p-2">
                          <input
                            className="w-full border rounded px-2 py-1 text-xs bg-background"
                            value={row.type}
                            onChange={e => setBomRows(prev => prev.map((r, i) => i === idx ? { ...r, type: e.target.value } : r))}
                          />
                        </td>
                        <td className="p-2">
                          <ComponentSearchInput
                            value={row.sku}
                            onChange={(sku, type, cost, desc) => setBomRows(prev => prev.map((r, i) =>
                              i === idx ? { ...r, sku, type: type || r.type, cost: cost || r.cost, desc: desc || r.desc } : r
                            ))}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            className="w-full border rounded px-2 py-1 text-xs bg-background text-muted-foreground"
                            value={row.desc}
                            onChange={e => setBomRows(prev => prev.map((r, i) => i === idx ? { ...r, desc: e.target.value } : r))}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="number"
                            className="w-full border rounded px-2 py-1 text-xs text-right bg-background"
                            min={1}
                            value={row.qty}
                            onChange={e => setBomRows(prev => prev.map((r, i) => i === idx ? { ...r, qty: parseFloat(e.target.value) || 1 } : r))}
                          />
                        </td>
                        {canViewCosts && (
                          <td className="p-2">
                            <input
                              type="number"
                              className="w-full border rounded px-2 py-1 text-xs text-right bg-background font-mono"
                              step="0.01"
                              value={row.cost}
                              onChange={e => setBomRows(prev => prev.map((r, i) => i === idx ? { ...r, cost: parseFloat(e.target.value) || 0 } : r))}
                            />
                          </td>
                        )}
                        <td className="p-2 text-center">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setBomRows(prev => prev.filter((_, i) => i !== idx))}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {/* Draft row */}
                    <tr className="bg-muted/20">
                      <td className="p-2">
                        <input
                          className="w-full border rounded px-2 py-1 text-xs bg-background"
                          placeholder="Type"
                          value={draftType}
                          onChange={e => setDraftType(e.target.value)}
                        />
                      </td>
                      <td className="p-2">
                        <ComponentSearchInput
                          value={draftSku}
                          onChange={(sku, type, cost, desc) => {
                            setDraftSku(sku);
                            if (type) setDraftType(type);
                            if (cost) setDraftCost(cost);
                            if (desc) setDraftDesc(desc);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          className="w-full border rounded px-2 py-1 text-xs bg-background text-muted-foreground"
                          placeholder="Description"
                          value={draftDesc}
                          readOnly
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          className="w-full border rounded px-2 py-1 text-xs text-right bg-background"
                          min={1}
                          value={draftQty}
                          onChange={e => setDraftQty(parseFloat(e.target.value) || 1)}
                        />
                      </td>
                      {canViewCosts && (
                        <td className="p-2">
                          <input
                            type="number"
                            className="w-full border rounded px-2 py-1 text-xs text-right bg-background font-mono"
                            step="0.01"
                            value={draftCost}
                            onChange={e => setDraftCost(parseFloat(e.target.value) || 0)}
                          />
                        </td>
                      )}
                      <td className="p-2 text-center">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={commitDraftRow} disabled={!draftSku.trim()}>
                          <Plus className="h-3.5 w-3.5 text-green-600" />
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">Search by part number or description. Click + to add row.</p>
            </div>
          ) : (
            /* ── Read-only BOM table ── */
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    {canViewCosts && <th className="text-center p-3 font-medium w-[40px]" title="Active configuration">Config <HelpTooltip tipKey="build.activeConfig" /></th>}
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-left p-3 font-medium">Part Number</th>
                    <th className="text-left p-3 font-medium">Description</th>
                    {canViewCosts && <th className="text-left p-3 font-medium">Supplier</th>}
                    <th className="text-right p-3 font-medium">Qty</th>
                    {canViewCosts && (
                      <>
                        <th className="text-right p-3 font-medium">Unit Cost</th>
                        <th className="text-right p-3 font-medium">Line Total</th>
                      </>
                    )}
                    <th className="text-right p-3 font-medium">Available</th>
                    <th className="text-center p-3 font-medium">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {(savedBom ?? []).filter((bc: any) => {
                    if (!isMarketingOnly) return true;
                    return !hiddenCategories.has((bc.type || '').toLowerCase());
                  }).map((bc: any, idx: number) => {
                    const comp = bc.component;
                    const qty = bc.qty || 1;
                    const cost = bc.cost || 0;
                    const avail = comp?.available_qty ?? null;

                    let stockStatus: 'ok' | 'low' | 'out' | 'unknown' = 'unknown';
                    if (comp) {
                      if (avail === 0) stockStatus = 'out';
                      else if (avail < qty) stockStatus = 'low';
                      else stockStatus = 'ok';
                    }

                    const isActive = activeSkus?.has(bc.sku) ?? true;

                    return (
                      <tr key={idx} className={cn('border-b transition-colors', isActive ? 'hover:bg-muted/50' : 'opacity-40 hover:bg-muted/30')}>
                        {canViewCosts && (
                          <td className="p-3 text-center">
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() => {
                                setActiveSkus(prev => {
                                  const next = new Set(prev);
                                  if (next.has(bc.sku)) next.delete(bc.sku);
                                  else next.add(bc.sku);
                                  // Persist to DB
                                  const csv = [...next].join(',');
                                  supabase.from('builds').update({ active_config_skus: csv }).eq('id', id).then(() => {
                                    queryClient.invalidateQueries({ queryKey: ['build', id] });
                                  });
                                  return next;
                                });
                              }}
                              className="h-3.5 w-3.5 rounded cursor-pointer"
                              title={isActive ? 'In active config' : 'Not in active config'}
                            />
                          </td>
                        )}
                        <td className="p-3"><Badge variant="outline" className="text-xs">{bc.type || '---'}</Badge></td>
                        <td className="p-3 font-mono text-xs">
                          {comp ? (
                            <Link to={`/components/${bc.sku}`} className="text-primary hover:underline">{bc.sku}</Link>
                          ) : (
                            <span className="text-muted-foreground">{bc.sku}</span>
                          )}
                        </td>
                        <td className="p-3 max-w-xs">
                          {isMarketingOnly ? (
                            <div className="flex items-start gap-1.5 group/desc">
                              <div className="flex-1 min-w-0">
                                <p className="truncate" title={b2cNames[bc.sku] || bc.desc || comp?.description}>
                                  {b2cNames[bc.sku] || bc.desc || comp?.description || <span className="text-muted-foreground italic">Not in components table</span>}
                                </p>
                                {!b2cNames[bc.sku] && (
                                  <span className="text-[10px] text-orange-500">missing B2C name</span>
                                )}
                              </div>
                              <button
                                className="shrink-0 opacity-0 group-hover/desc:opacity-100 transition-opacity p-0.5 hover:text-primary"
                                title="Copy description"
                                onClick={() => {
                                  const text = b2cNames[bc.sku] || bc.desc || comp?.description || bc.sku;
                                  navigator.clipboard.writeText(text);
                                  setCopiedSku(bc.sku);
                                  setTimeout(() => setCopiedSku(null), 1500);
                                }}
                              >
                                {copiedSku === bc.sku
                                  ? <ClipboardCheck className="h-3.5 w-3.5 text-green-600" />
                                  : <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                                }
                              </button>
                            </div>
                          ) : (
                            <p className="truncate" title={bc.desc || comp?.description}>
                              {bc.desc || comp?.description || <span className="text-muted-foreground italic">Not in components table</span>}
                            </p>
                          )}
                        </td>
                        {canViewCosts && <td className="p-3 text-muted-foreground text-xs">{comp?.supplier || '---'}</td>}
                        <td className="p-3 text-right font-medium">{qty}</td>
                        {canViewCosts && (
                          <>
                            <td className="p-3 text-right font-mono text-xs">{formatCurrency(cost)}</td>
                            <td className="p-3 text-right font-mono text-xs font-medium">{formatCurrency(cost * qty)}</td>
                          </>
                        )}
                        <td className="p-3 text-right">
                          {avail != null ? (
                            <span className={cn(avail < qty ? 'text-red-600 font-medium' : '')}>{avail}</span>
                          ) : '---'}
                        </td>
                        <td className="p-3 text-center">
                          <Badge className={cn('text-xs',
                            stockStatus === 'ok' ? 'bg-green-100 text-green-700' :
                            stockStatus === 'low' ? 'bg-yellow-100 text-yellow-700' :
                            stockStatus === 'out' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-500'
                          )}>
                            {stockStatus === 'ok' ? 'OK' : stockStatus === 'low' ? 'Low' : stockStatus === 'out' ? 'Out' : '---'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {canViewCosts && (savedBom?.length ?? 0) > 0 && (
                  <tfoot>
                    <tr className="bg-muted/50 font-medium">
                      <td colSpan={7} className="p-3 text-right">Active Config Cost:</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(totals.totalCost)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
              {(!savedBom?.length) && (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  No components in this build
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Create button (new builds only) ── */}
      {isNew && (
        <div className="flex gap-3">
          <Button onClick={() => createBuildMutation.mutate()} disabled={createBuildMutation.isPending || !headerForm.sku.trim()}>
            <Save className="h-4 w-4 mr-2" />
            {createBuildMutation.isPending ? 'Creating...' : 'Create Build'}
          </Button>
          <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        </div>
      )}

      {/* ── Assembly History (existing builds, view mode only) ── */}
      {!isNew && !editMode && assemblyHistory && assemblyHistory.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />Assembly History ({assemblyHistory.length})
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setAssemblyHistoryExpanded(!assemblyHistoryExpanded)}>
                {assemblyHistoryExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          {assemblyHistoryExpanded && (
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-3 font-medium">Order #</th>
                      <th className="pb-2 pr-3 font-medium">Serial #</th>
                      <th className="pb-2 pr-3 font-medium">Technician</th>
                      <th className="pb-2 pr-3 font-medium">Status</th>
                      <th className="pb-2 pr-3 font-medium">Type</th>
                      <th className="pb-2 pr-3 font-medium">TO #</th>
                      <th className="pb-2 pr-3 font-medium text-center">Qty</th>
                      <th className="pb-2 pr-3 font-medium">Created</th>
                      <th className="pb-2 font-medium">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assemblyHistory.map((order: any) => (
                      <tr key={order.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 pr-3 font-mono text-xs">{order.order_number || '—'}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{order.serial_number || '—'}</td>
                        <td className="py-2 pr-3 text-xs">{order.technician?.name || '—'}</td>
                        <td className="py-2 pr-3">
                          <Badge className={cn('text-xs', getStatusColor(order.status))}>
                            {order.status?.replace(/_/g, ' ') || '—'}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-xs">{order.build_type && order.build_type !== 'Stock' ? order.build_type : '—'}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{order.to_number || '—'}</td>
                        <td className="py-2 pr-3 text-xs text-center">{order.quantity ?? 1}</td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {order.created_at ? new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {order.completed_at ? new Date(order.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* ── Media Bank (existing builds only) ── */}
      {!isNew && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />Media Bank
                </CardTitle>
                {allMedia.length > 0 && (
                  <span className="text-sm text-muted-foreground">{allMedia.length} image{allMedia.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleMediaUpload}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMediaBankOpen(true)}
                    >
                      <FolderOpen className="h-4 w-4 mr-1" />
                      Media Bank
                    </Button>
                  </>
                )}
                <Button variant="ghost" size="sm" onClick={() => setMediaExpanded(!mediaExpanded)}>
                  {mediaExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>
          {mediaExpanded && (
            <CardContent>
              {allMedia.length === 0 ? (
                <div className="text-center py-8">
                  <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                  <p className="text-sm text-muted-foreground">No images uploaded yet</p>
                  {isAdmin && <p className="text-xs text-muted-foreground mt-1">Upload build images here, or upload case photos from the case component page</p>}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {allMedia.map((img: any) => (
                    <div key={`${img.source || 'build'}-${img.id}`} className="group relative border rounded-lg overflow-hidden bg-muted/30">
                      <img
                        src={img.url}
                        alt={img.file_name || 'Build image'}
                        className="w-full h-40 object-cover cursor-pointer"
                        loading="lazy"
                        onClick={() => setLightboxUrl(img.url)}
                      />
                      <div className="p-2">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs text-muted-foreground truncate flex-1" title={img.file_name}>{img.file_name}</p>
                          {img.source === 'case' && (
                            <span className="text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded shrink-0">Case</span>
                          )}
                          {img.media_bank_id && (
                            <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded shrink-0">Shared</span>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(img.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                      {isAdmin && !img.source && (
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => setDeleteImageId(img.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* ── Delete build confirmation (super_admin) ── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader><CardTitle className="text-red-600">Delete Build</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p>Are you sure you want to delete <strong>{build?.sku}</strong>?</p>
              <p className="text-sm text-muted-foreground">This will also remove all BOM components and media. This cannot be undone.</p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                <Button variant="destructive" onClick={() => deleteBuildMutation.mutate()} disabled={deleteBuildMutation.isPending}>
                  {deleteBuildMutation.isPending ? 'Deleting…' : 'Delete'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Delete image confirmation ── */}
      <Dialog open={!!deleteImageId} onOpenChange={(open) => { if (!open) setDeleteImageId(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete image?</DialogTitle>
            <DialogDescription>This will permanently remove the image from the media bank.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteImageId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteImageMutation.isPending}
              onClick={() => deleteImageId && deleteImageMutation.mutate(deleteImageId)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Web Listing Editor ── */}
      <Dialog open={webListingOpen} onOpenChange={(open) => {
        if (!open) { setWebListingOpen(false); setWebListingHtmlMode(false); }
      }}>
        <DialogContent className={cn(
          'flex flex-col overflow-hidden transition-all duration-200',
          webListingMaximized
            ? 'sm:max-w-[95vw] max-h-[95vh] w-[95vw] h-[95vh]'
            : 'sm:max-w-4xl max-h-[90vh]'
        )}>
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Web Listing — {build?.sku}
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setWebListingMaximized(prev => !prev)}
                title={webListingMaximized ? 'Restore' : 'Maximize'}
              >
                {webListingMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>
            <DialogDescription>
              Compose web listing content. Use the toolbar to format text, then copy/paste into your CMS or download as Word document.
            </DialogDescription>
          </DialogHeader>

          {/* Formatting toolbar */}
          <div className="flex flex-wrap gap-1 border rounded-lg p-1.5 bg-muted/30">
            {[
              { icon: Bold, cmd: 'bold', title: 'Bold' },
              { icon: Italic, cmd: 'italic', title: 'Italic' },
              { icon: Underline, cmd: 'underline', title: 'Underline' },
            ].map(({ icon: Icon, cmd, title }) => (
              <Button
                key={cmd}
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                title={title}
                onMouseDown={(e) => {
                  e.preventDefault();
                  document.execCommand(cmd);
                  editorRef.current?.focus();
                }}
              >
                <Icon className="h-4 w-4" />
              </Button>
            ))}
            <div className="w-px bg-border mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title="Heading 1"
              onMouseDown={(e) => {
                e.preventDefault();
                document.execCommand('formatBlock', false, 'h1');
                editorRef.current?.focus();
              }}
            >
              <Heading1 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title="Heading 2"
              onMouseDown={(e) => {
                e.preventDefault();
                document.execCommand('formatBlock', false, 'h2');
                editorRef.current?.focus();
              }}
            >
              <Heading2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title="Normal text"
              onMouseDown={(e) => {
                e.preventDefault();
                document.execCommand('formatBlock', false, 'p');
                editorRef.current?.focus();
              }}
            >
              <Type className="h-4 w-4" />
            </Button>
            <div className="w-px bg-border mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title="Bullet list"
              onMouseDown={(e) => {
                e.preventDefault();
                document.execCommand('insertUnorderedList');
                editorRef.current?.focus();
              }}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title="Numbered list"
              onMouseDown={(e) => {
                e.preventDefault();
                document.execCommand('insertOrderedList');
                editorRef.current?.focus();
              }}
            >
              <ListOrdered className="h-4 w-4" />
            </Button>
            <div className="w-px bg-border mx-1" />
            <Button
              type="button"
              variant={webListingHtmlMode ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 px-2 text-xs font-mono"
              title="Toggle HTML source view"
              onClick={() => {
                if (!webListingHtmlMode) {
                  // Rich text → HTML source
                  if (htmlSourceRef.current && editorRef.current) {
                    htmlSourceRef.current.value = editorRef.current.innerHTML;
                  }
                } else {
                  // HTML source → Rich text
                  if (editorRef.current && htmlSourceRef.current) {
                    editorRef.current.innerHTML = htmlSourceRef.current.value;
                  }
                }
                setWebListingHtmlMode(m => !m);
              }}
            >
              <Code2 className="h-4 w-4 mr-1" />HTML
            </Button>
          </div>

          {/* Editor area */}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className={cn(
              'flex-1 overflow-y-auto border rounded-lg p-4 bg-white text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 prose prose-sm max-w-none',
              webListingMaximized ? 'min-h-0' : 'min-h-[400px] max-h-[55vh]',
              webListingHtmlMode ? 'hidden' : ''
            )}
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            onPaste={(e) => {
              // Allow paste but strip problematic HTML attributes
              const html = e.clipboardData.getData('text/html');
              if (html) {
                e.preventDefault();
                // Clean the HTML: remove class, style, and data attributes to prevent CMS issues
                const cleaned = html
                  .replace(/\s(class|style|data-[\w-]+)="[^"]*"/gi, '')
                  .replace(/\s(class|style|data-[\w-]+)='[^']*'/gi, '')
                  .replace(/<meta[^>]*>/gi, '')
                  .replace(/<link[^>]*>/gi, '')
                  .replace(/<!--[\s\S]*?-->/g, '');
                document.execCommand('insertHTML', false, cleaned);
              }
            }}
          />
          {/* HTML source view */}
          <textarea
            ref={htmlSourceRef}
            className={cn(
              'flex-1 overflow-y-auto border rounded-lg p-4 bg-muted/30 text-xs font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 resize-none',
              webListingMaximized ? 'min-h-0' : 'min-h-[400px] max-h-[55vh]',
              webListingHtmlMode ? '' : 'hidden'
            )}
            spellCheck={false}
            placeholder="Paste or type HTML here…"
          />

          <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const html = webListingHtmlMode
                    ? (htmlSourceRef.current?.value ?? '')
                    : (editorRef.current?.innerHTML ?? '');
                  setWebListingPreviewHtml(buildPreviewHtml(html));
                  setWebListingPreviewOpen(true);
                }}
              >
                <Eye className="h-4 w-4 mr-1" />Preview
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const rawHtml = webListingHtmlMode
                    ? (htmlSourceRef.current?.value ?? '')
                    : (editorRef.current?.innerHTML ?? '');
                  // Strip HTML-only blocks (APM banners, iframes, scripts, <br>) for Word export
                  const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
                  doc.querySelectorAll('script, iframe').forEach(el => el.remove());
                  // Remove any block element whose sole content is an iframe/script (APM wrapper divs)
                  doc.querySelectorAll('div, p, span').forEach(el => {
                    if (el.textContent?.trim() === '' && !el.querySelector('img')) el.remove();
                  });
                  const cleanedHtml = doc.body.innerHTML;
                  // Generate Word-compatible HTML
                  const wordHtml = `
                    <html xmlns:o="urn:schemas-microsoft-com:office:office"
                          xmlns:w="urn:schemas-microsoft-com:office:word"
                          xmlns="http://www.w3.org/TR/REC-html40">
                    <head>
                      <meta charset="utf-8">
                      <style>
                        body { font-family: Calibri, sans-serif; font-size: 11pt; line-height: 1.5; color: #333; }
                        h1 { font-size: 20pt; font-weight: bold; color: #111; margin: 12pt 0 6pt; }
                        h2 { font-size: 16pt; font-weight: bold; color: #222; margin: 10pt 0 5pt; }
                        h3 { font-size: 13pt; font-weight: bold; color: #333; margin: 8pt 0 4pt; }
                        p { margin: 0 0 6pt; }
                        ul, ol { margin: 4pt 0 8pt 20pt; }
                        li { margin: 2pt 0; }
                        img { max-width: 100%; }
                      </style>
                    </head>
                    <body>${cleanedHtml}</body>
                    </html>`;
                  const blob = new Blob([wordHtml], { type: 'application/msword' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${build?.sku || 'web-listing'}_web-details.doc`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                <Download className="h-4 w-4 mr-1" />Download as Word
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setWebListingOpen(false)}>Close</Button>
              <Button
                onClick={() => {
                  const html = webListingHtmlMode
                    ? (htmlSourceRef.current?.value ?? '')
                    : (editorRef.current?.innerHTML ?? '');
                  saveWebListingMutation.mutate(html);
                }}
                disabled={saveWebListingMutation.isPending}
              >
                <Save className="h-4 w-4 mr-1" />
                {saveWebListingMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Web Listing Preview ── */}
      <Dialog open={webListingPreviewOpen} onOpenChange={(o) => { setWebListingPreviewOpen(o); if (!o) setWebListingPreviewMaximized(false); }}>
        <DialogContent className={webListingPreviewMaximized ? 'max-w-[100vw] w-screen h-screen max-h-[100vh] rounded-none flex flex-col' : 'sm:max-w-5xl max-h-[92vh] flex flex-col'}>
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Preview — {build?.sku}
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setWebListingPreviewMaximized(prev => !prev)}
                title={webListingPreviewMaximized ? 'Restore' : 'Maximise'}
              >
                {webListingPreviewMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden rounded border">
            <iframe
              srcDoc={webListingPreviewHtml}
              className="w-full h-full"
              title="Web listing preview"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWebListingPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Media Bank dialog ── */}
      <Dialog open={mediaBankOpen} onOpenChange={setMediaBankOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Media Bank</DialogTitle>
            <DialogDescription>Shared images you can reuse across builds</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or label…"
                className="pl-9"
                value={bankSearch}
                onChange={e => setBankSearch(e.target.value)}
              />
            </div>
            {isAdmin && (
              <>
                <input
                  ref={bankFileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleBankUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bankFileRef.current?.click()}
                  disabled={bankUploading}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  {bankUploading ? 'Uploading…' : 'Upload to Bank'}
                </Button>
              </>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredBankImages.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">
                  {mediaBankImages.length === 0 ? 'No shared images yet — upload some to get started' : 'No images match your search'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredBankImages.map((img: any) => {
                  const isLinked = linkedBankIds.has(img.id);
                  return (
                    <div key={img.id} className="group relative border rounded-lg overflow-hidden bg-muted/30">
                      <img
                        src={img.url}
                        alt={img.label || img.file_name}
                        className="w-full h-36 object-cover cursor-pointer"
                        loading="lazy"
                        onClick={() => setLightboxUrl(img.url)}
                      />
                      <div className="p-2">
                        <p className="text-xs text-muted-foreground truncate" title={img.label || img.file_name}>
                          {img.label || img.file_name}
                        </p>
                      </div>
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!isLinked ? (
                          <Button size="icon" className="h-7 w-7" onClick={() => linkBankImage(img)}>
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Badge className="bg-green-600 text-white text-[10px] pointer-events-none">Linked</Badge>
                        )}
                        {isAdmin && (
                          <Button variant="destructive" size="icon" className="h-7 w-7" onClick={() => deleteBankImage(img.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Display Names dialog ── */}
      <Dialog open={showDisplayNamesDialog} onOpenChange={(open) => { if (!open) { setShowDisplayNamesDialog(false); setEditedDisplayNames({}); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Type className="h-5 w-5" />Display Names — {build?.sku}
            </DialogTitle>
            <DialogDescription>
              These names appear on the HUD overlay, spec sheet, and marketing tag. Edits apply across all builds sharing the same component.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background border-b">
                <tr>
                  <th className="text-left p-3 font-medium w-[80px]">Type</th>
                  <th className="text-left p-3 font-medium w-[160px]">Part No.</th>
                  <th className="text-left p-3 font-medium">Display Name (B2C)</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(savedBom ?? []).map((bc: any) => {
                  const current = editedDisplayNames[bc.sku] ?? b2cNames[bc.sku] ?? bc.desc ?? bc.component?.description ?? '';
                  const isMissing = !b2cNames[bc.sku] && !(bc.sku in editedDisplayNames);
                  return (
                    <tr key={bc.sku} className="hover:bg-muted/30">
                      <td className="p-3"><Badge variant="outline" className="text-xs">{bc.type || '---'}</Badge></td>
                      <td className="p-3 font-mono text-xs text-muted-foreground">{bc.sku}</td>
                      <td className="p-3">
                        <input
                          className={cn('w-full border rounded px-2 py-1 text-xs bg-background', isMissing && 'border-orange-300')}
                          value={current}
                          placeholder={bc.desc || bc.component?.description || 'Enter display name…'}
                          onChange={e => setEditedDisplayNames(prev => ({ ...prev, [bc.sku]: e.target.value }))}
                        />
                        {isMissing && <span className="text-[10px] text-orange-500">no B2C name saved yet</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowDisplayNamesDialog(false); setEditedDisplayNames({}); }}>Cancel</Button>
            <Button
              disabled={!displayNamesDirty || saveDisplayNamesMutation.isPending}
              onClick={() => saveDisplayNamesMutation.mutate(undefined, { onSuccess: () => setShowDisplayNamesDialog(false) })}
            >
              <Save className="h-4 w-4 mr-1" />
              {saveDisplayNamesMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Image lightbox ── */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center cursor-pointer"
          onClick={() => setLightboxUrl(null)}
        >
          <img
            src={lightboxUrl}
            alt="Full size"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl font-light leading-none"
            onClick={() => setLightboxUrl(null)}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
